<script lang="ts" setup>
import CopyToClipboard from '@shell/components/Resource/Detail/CopyToClipboard.vue';
import Content from '@shell/components/Resource/Detail/Preview/Content.vue';
import { useBasicSetupFocusTrap } from '@shell/composables/focusTrap';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { metadataPreviewPosition } from '@shell/utils/metadata-preview-position';

export interface Props {
  title: string;
  value: string;
  anchorElement: HTMLElement | null;
}

defineOptions({ inheritAttrs: false });

const props = defineProps<Props>();
const emit = defineEmits<{(e: 'close', keyboardExit: boolean): void}>();
const top = ref('12px');
const left = ref('12px');
const containerRef = ref<HTMLElement | null>(null);
const escapePressed = ref(false);
const isMouseInteraction = ref(false);
let resizeObserver: ResizeObserver | undefined;

const updatePosition = () => {
  const width = window.innerWidth || document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;
  const rect = containerRef.value?.getBoundingClientRect();
  const position = metadataPreviewPosition(props.anchorElement?.getBoundingClientRect(), { width, height }, { width: rect?.width || Math.min(500, Math.max(0, width - 24)), height: rect?.height || Math.min(200, Math.max(0, height - 24)) });

  top.value = `${ position.top }px`;
  left.value = `${ position.left }px`;
};

const onFocusOut = (e: FocusEvent) => {
  // Refocus the container if the user clicks a child element (copy to clipboard)
  if (!escapePressed.value && containerRef.value?.contains(e.relatedTarget as Node)) {
    if (isMouseInteraction.value) {
      containerRef.value.focus();
    }
  } else {
    emit('close', escapePressed.value);
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    escapePressed.value = true;
    containerRef.value?.blur();
  }
};

onMounted(() => {
  updatePosition();
  if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
    resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(containerRef.value);
  }
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition, true);
  containerRef.value?.focus();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition, true);
});

useBasicSetupFocusTrap('#focus-trap-preview-container-element');

</script>
<template>
  <Teleport to="#preview">
    <div
      id="focus-trap-preview-container-element"
      ref="containerRef"
      class="preview"
      tabindex="-1"
      @keydown="onKeydown"
      @focusout="onFocusOut"
      @mousedown="isMouseInteraction=true"
      @mouseup="isMouseInteraction=false"
    >
      <div class="title">
        {{ props.title }}
      </div>
      <Content
        class="content"
        :value="props.value"
      />
      <CopyToClipboard
        class="copy-to-clipboard"
        :value="props.value"
      />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.preview-mouse-catcher {
  cursor: default;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 120;
}
.preview {
  cursor: default;
  position: fixed;
  left: v-bind(left);
  top: v-bind(top);
  z-index: 121;
  display: flex;
  flex-direction: column;
  width: min(500px, calc(100vw - 24px));
  min-width: 0;
  max-height: min(550px, calc(100vh - 24px));
  box-sizing: border-box;

  padding: 16px;

  background-color: var(--body-bg);
  border: 1px solid var(--border);
  border-radius: var(--border-radius-md);

  &:focus {
    outline: none;
  }

  .title {
    margin-bottom: 16px;
    padding-right: 36px;
    overflow-wrap: anywhere;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  }

  .content {
    flex: 1;

    min-height: 0;
    overflow: auto;
    overflow-wrap: anywhere;
  }

  .copy-to-clipboard {
      position: absolute;
      right: 12px;
      top: 12px;
  }
}
</style>
